import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { DayHeader } from "./DayHeader";
import { TodaysFocus } from "./TodaysFocus";
import { RamadanDashboard } from "@/components/RamadanDashboard";
import { TrackerCard } from "./TrackerCard";
import { QuickActions } from "./QuickActions";
import { PeriodCards } from "./PeriodCards";
import { ShareProgressCard } from "./ShareProgressCard";
import { PrayerTracker } from "@/components/trackers/PrayerTracker";
import { QuranTracker } from "@/components/trackers/QuranTracker";
import { FastingTracker } from "@/components/trackers/FastingTracker";
import { GoodDeedsTracker } from "@/components/trackers/GoodDeedsTracker";
import { DhikrTracker } from "@/components/trackers/DhikrTracker";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { getPeriodForDay } from "@/data/dailyDuas";
import { UpgradeAccountNudge } from "@/components/UpgradeAccountNudge";
import { LiveUserCount } from "@/components/LiveUserCount";

interface GoodDeed {
  id: string;
  text: string;
  day: number;
  timestamp: number;
}

interface QuranProgress {
  pages: number;
  juz: number;
  lastSurah?: number;
  lastAyah?: number;
  completedSurahs?: number[];
}

interface TrackerData {
  prayers: Record<number, string[]>;
  quranProgress: Record<number, QuranProgress>;
  fastedDays: number[];
  missedFastDays: number[];
  goodDeeds: GoodDeed[];
  dhikrCounts: Record<number, Record<string, number>>;
}

interface UnifiedDashboardProps {
  currentDay: number;
  completedDays: number[];
  trackerData: TrackerData;
  onDayChange: (day: number) => void;
  onTrackerUpdate: (updates: Partial<TrackerData>) => void;
  onNavigate: (view: string, day?: number) => void;
  onLogout: () => void;
}

type TrackerType = "prayer" | "quran" | "fasting" | "deeds" | "dhikr";

const TRACKER_CONFIG: Record<TrackerType, { title: string; icon: string; color: string }> = {
  prayer: { title: "Prayer", icon: "🕌", color: "hsl(152, 60%, 45%)" },
  quran: { title: "Quran", icon: "📖", color: "hsl(42, 70%, 50%)" },
  fasting: { title: "Fasting", icon: "🌙", color: "hsl(280, 60%, 55%)" },
  deeds: { title: "Good Deeds", icon: "💚", color: "hsl(340, 65%, 55%)" },
  dhikr: { title: "Dhikr", icon: "📿", color: "hsl(170, 60%, 45%)" },
};

export const UnifiedDashboard = ({
  currentDay,
  completedDays,
  trackerData,
  onDayChange,
  onTrackerUpdate,
  onNavigate,
  onLogout,
}: UnifiedDashboardProps) => {
  const { user, isGuest } = useAuth();
  const { profile, updateProfile } = useProfile();
  const [expandedTracker, setExpandedTracker] = useState<TrackerType | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showTrackers, setShowTrackers] = useState(true);
  const [showShareCard, setShowShareCard] = useState(false);
  const [ramadanStartInput, setRamadanStartInput] = useState(profile?.ramadanStartDate || '');

  const period = getPeriodForDay(currentDay);

  // Calculate streak
  const streak = useMemo(() => {
    let count = 0;
    for (let day = currentDay; day >= 1; day--) {
      const prayers = trackerData.prayers[day] || [];
      if (prayers.length >= 3) {
        count++;
      } else if (day < currentDay) {
        break;
      }
    }
    return count;
  }, [trackerData.prayers, currentDay]);

  // Calculate progress for each tracker
  const getTrackerProgress = (tracker: TrackerType): number => {
    switch (tracker) {
      case "prayer": {
        const todayPrayers = trackerData.prayers[currentDay]?.length || 0;
        return (todayPrayers / 5) * 100;
      }
      case "quran": {
        const todayProgress = trackerData.quranProgress[currentDay];
        if (!todayProgress) return 0;
        return Math.min((todayProgress.pages / 20) * 100, 100);
      }
      case "fasting": {
        return trackerData.fastedDays.includes(currentDay) ? 100 : 0;
      }
      case "deeds": {
        const todayDeeds = trackerData.goodDeeds.filter(d => d.day === currentDay).length;
        return Math.min((todayDeeds / 3) * 100, 100);
      }
      case "dhikr": {
        const todayCounts = trackerData.dhikrCounts[currentDay] || {};
        const totalToday = Object.values(todayCounts).reduce((sum, c) => sum + c, 0);
        return Math.min((totalToday / 400) * 100, 100);
      }
      default:
        return 0;
    }
  };

  const getTrackerSubtitle = (tracker: TrackerType): string => {
    switch (tracker) {
      case "prayer": {
        const count = trackerData.prayers[currentDay]?.length || 0;
        return `${count}/5 prayers today`;
      }
      case "quran": {
        const progress = trackerData.quranProgress[currentDay];
        return progress ? `${progress.pages} pages • Juz ${progress.juz}` : "Track your reading";
      }
      case "fasting": {
        const fasted = trackerData.fastedDays.length;
        return `${fasted} days fasted`;
      }
      case "deeds": {
        const count = trackerData.goodDeeds.filter(d => d.day === currentDay).length;
        return count > 0 ? `${count} deeds today` : "Log your good deeds";
      }
      case "dhikr": {
        const counts = trackerData.dhikrCounts[currentDay] || {};
        const total = Object.values(counts).reduce((sum, c) => sum + c, 0);
        return total > 0 ? `${total} dhikr today` : "Start your dhikr";
      }
      default:
        return "";
    }
  };

  const toggleTracker = (tracker: TrackerType) => {
    setExpandedTracker(expandedTracker === tracker ? null : tracker);
  };

  // Tracker handlers
  const handlePrayerToggle = (day: number, prayer: string) => {
    const dayPrayers = trackerData.prayers[day] || [];
    const newPrayers = dayPrayers.includes(prayer)
      ? dayPrayers.filter(p => p !== prayer)
      : [...dayPrayers, prayer];
    onTrackerUpdate({ prayers: { ...trackerData.prayers, [day]: newPrayers } });
  };

  const handleQuranUpdate = (day: number, data: QuranProgress) => {
    onTrackerUpdate({ 
      quranProgress: { ...trackerData.quranProgress, [day]: data } 
    });
  };

  const handleFastedToggle = (day: number) => {
    const newFasted = trackerData.fastedDays.includes(day)
      ? trackerData.fastedDays.filter(d => d !== day)
      : [...trackerData.fastedDays, day];
    const newMissed = trackerData.missedFastDays.filter(d => d !== day);
    onTrackerUpdate({ fastedDays: newFasted, missedFastDays: newMissed });
  };

  const handleMissedToggle = (day: number) => {
    const newMissed = trackerData.missedFastDays.includes(day)
      ? trackerData.missedFastDays.filter(d => d !== day)
      : [...trackerData.missedFastDays, day];
    onTrackerUpdate({ missedFastDays: newMissed });
  };

  const handleAddDeed = (text: string, day: number) => {
    const newDeed: GoodDeed = {
      id: Date.now().toString(),
      text,
      day,
      timestamp: Date.now(),
    };
    onTrackerUpdate({ goodDeeds: [...trackerData.goodDeeds, newDeed] });
  };

  const handleRemoveDeed = (id: string) => {
    onTrackerUpdate({ goodDeeds: trackerData.goodDeeds.filter(d => d.id !== id) });
  };

  const handleDhikrUpdate = (day: number, dhikrId: string, count: number) => {
    const dayDhikr = trackerData.dhikrCounts[day] || {};
    onTrackerUpdate({
      dhikrCounts: { ...trackerData.dhikrCounts, [day]: { ...dayDhikr, [dhikrId]: count } }
    });
  };

  const handleSaveRamadanDate = () => {
    updateProfile({ ramadanStartDate: ramadanStartInput });
    setShowSettings(false);
  };

  const trackers: TrackerType[] = ["prayer", "quran", "fasting", "deeds", "dhikr"];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Header with Day Navigation */}
        <DayHeader
          currentDay={currentDay}
          ramadanStartDate={profile?.ramadanStartDate}
          streak={streak}
          onDayChange={onDayChange}
          onSettings={() => setShowSettings(!showSettings)}
          onLogout={onLogout}
          userName={profile?.name || user?.email?.split('@')[0]}
          isGuest={isGuest}
        />

        {/* Live user count */}
        <div className="flex justify-center">
          <LiveUserCount />
        </div>

        {/* Settings Panel */}
        <AnimatePresence>
          {showSettings && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-muted rounded-lg p-4 space-y-4 overflow-hidden"
            >
              <h3 className="font-display text-lg text-foreground">Settings</h3>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Ramadan Start Date</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={ramadanStartInput}
                    onChange={(e) => setRamadanStartInput(e.target.value)}
                    className="flex-1 px-3 py-2 border border-border rounded-md text-sm bg-background"
                  />
                  <button 
                    onClick={handleSaveRamadanDate}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium"
                  >
                    Save
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Period Cards - Days of Mercy/Forgiveness/Salvation */}
        <PeriodCards 
          currentDay={currentDay} 
        />

        {/* Guest upgrade nudge — shown only for guests */}
        {isGuest && currentDay >= 1 && (
          <UpgradeAccountNudge variant="card" />
        )}

        {/* Today's Focus - Daily Dua + Affirmation */}
        <TodaysFocus
          currentDay={currentDay}
          onViewDua={() => onNavigate("daily", currentDay)}
        />

        {/* Tracker Cards Grid - Collapsible */}
        <section className="space-y-3">
          <button
            onClick={() => setShowTrackers(!showTrackers)}
            className="w-full flex items-center justify-between px-1 py-2 hover:bg-accent/30 rounded-md transition-colors"
          >
            <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Today's Trackers
            </h2>
            <motion.div
              animate={{ rotate: showTrackers ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </motion.div>
          </button>
          
          <AnimatePresence>
            {showTrackers && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="space-y-3 overflow-hidden"
              >
                {trackers.map((tracker) => (
                  <TrackerCard
                    key={tracker}
                    title={TRACKER_CONFIG[tracker].title}
                    icon={TRACKER_CONFIG[tracker].icon}
                    progress={getTrackerProgress(tracker)}
                    color={TRACKER_CONFIG[tracker].color}
                    subtitle={getTrackerSubtitle(tracker)}
                    onClick={() => toggleTracker(tracker)}
                    isExpanded={expandedTracker === tracker}
                  >
                    {tracker === "prayer" && (
                      <PrayerTracker
                        day={currentDay}
                        prayers={trackerData.prayers}
                        onToggle={handlePrayerToggle}
                      />
                    )}
                    {tracker === "quran" && (
                      <QuranTracker
                        day={currentDay}
                        progress={trackerData.quranProgress}
                        onUpdate={handleQuranUpdate}
                      />
                    )}
                    {tracker === "fasting" && (
                      <FastingTracker
                        completedDays={trackerData.fastedDays}
                        missedDays={trackerData.missedFastDays}
                        onToggleFasted={handleFastedToggle}
                        onToggleMissed={handleMissedToggle}
                      />
                    )}
                    {tracker === "deeds" && (
                      <GoodDeedsTracker
                        deeds={trackerData.goodDeeds}
                        currentDay={currentDay}
                        onAddDeed={handleAddDeed}
                        onRemoveDeed={handleRemoveDeed}
                      />
                    )}
                    {tracker === "dhikr" && (
                      <DhikrTracker
                        day={currentDay}
                        counts={trackerData.dhikrCounts}
                        onUpdateCount={handleDhikrUpdate}
                      />
                    )}
                  </TrackerCard>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </section>

        {/* Progress Dashboard */}
         <section>
          <RamadanDashboard
            trackerData={trackerData}
            completedDays={completedDays}
            currentDay={currentDay}
          />
        </section>

        {/* Quick Actions */}
        <section className="space-y-3">
          <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground px-1">
            Quick Actions
          </h2>
          <QuickActions
            currentDay={currentDay}
            showLaylatulQadr={period?.id === 3}
            onOpenJournal={() => onNavigate("journal")}
            onOpenCalendar={() => onNavigate("calendar")}
            onOpenLaylatulQadr={() => onNavigate("laylatul-qadr")}
            onOpenPeriods={() => onNavigate("periods")}
            onShareProgress={() => setShowShareCard(true)}
          />
        </section>

        {/* Share Progress Card Modal */}
        <ShareProgressCard
          open={showShareCard}
          onOpenChange={setShowShareCard}
          currentDay={currentDay}
          streak={streak}
          progress={{
            prayer: getTrackerProgress("prayer"),
            quran: getTrackerProgress("quran"),
            fasting: getTrackerProgress("fasting"),
            dhikr: getTrackerProgress("dhikr"),
            deeds: getTrackerProgress("deeds"),
          }}
          userName={profile?.name || user?.email?.split('@')[0]}
        />

        {/* Day Selector Mini */}
        <section className="bg-card border border-border/50 rounded-lg p-4">
          <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground text-center mb-3">
            Jump to Day
          </h2>
          <div className="flex flex-wrap justify-center gap-1.5">
            {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => {
              const dayPeriod = getPeriodForDay(day);
              const isOddLast10 = dayPeriod?.id === 3 && day % 2 === 1;
              const isCompleted = completedDays.includes(day);
              
              return (
                <button
                  key={day}
                  onClick={() => onDayChange(day)}
                  className={`w-7 h-7 rounded-full text-xs font-medium transition-all flex items-center justify-center ${
                    day === currentDay
                      ? 'bg-primary text-primary-foreground scale-110 shadow-md'
                      : isCompleted
                      ? 'bg-emerald-500 text-white'
                      : isOddLast10
                      ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      : 'bg-muted text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </section>

        {/* Donation link */}
        <div className="text-center pt-4 pb-2">
          <a
            href="https://buy.stripe.com/bJecN59MH0xv79x5xh14400"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs tracking-[0.12em] uppercase text-muted-foreground hover:text-foreground transition-colors"
          >
            ♡ Support Layali
          </a>
        </div>
      </div>
    </div>
  );
};
